import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiCheckBold, PiXBold, PiEyeBold, PiEnvelopeSimpleBold } from 'react-icons/pi';
import { Link, useSearchParams } from 'react-router-dom';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import { PageHeader, LoadingState } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import api from '@v2/lib/api';
import type { CommissionRecord } from './types';
import { ROLE_LABELS } from './types';
import { COMMISSION_LIVE_QUERY } from '@v2/hooks/commissionLiveQuery';

type PendingAction =
    | { type: 'request_nf_bulk'; count: number }
    | { type: 'mark_paid'; recordId: number }
    | { type: 'cancel'; recordId: number };

const STATUS_BADGE: Record<string, string> = {
    pending:      'bg-gray-100 text-gray-700',
    awaiting_nf:  'bg-yellow-100 text-yellow-700',
    nf_submitted: 'bg-blue-100 text-blue-700',
    paid:         'bg-green-100 text-green-700',
    cancelled:    'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
    pending:      'Pendente',
    awaiting_nf:  'Aguardando NF',
    nf_submitted: 'NF Enviada',
    paid:         'Pago',
    cancelled:    'Cancelado',
};

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function modalCopy(action: PendingAction) {
    switch (action.type) {
        case 'request_nf_bulk':
            return {
                title: 'Cobrar NF em lote?',
                description: `Será enviado e-mail de cobrança para ${action.count} registro(s) aguardando NF.`,
                confirmLabel: 'Enviar todos',
            };
        case 'mark_paid':
            return {
                title: 'Confirmar pagamento?',
                description: 'O registro será marcado como pago após a confirmação.',
                confirmLabel: 'Confirmar pagamento',
            };
        case 'cancel':
            return {
                title: 'Cancelar comissão?',
                description: 'Esta ação não pode ser desfeita.',
                confirmLabel: 'Cancelar comissão',
            };
    }
}

export default function CommissionRecordsPage() {
    const qc = useQueryClient();
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        status: searchParams.get('status') ?? '',
        role: searchParams.get('role') ?? '',
        from: searchParams.get('from') ?? '',
        to: searchParams.get('to') ?? '',
        per_page: 25,
        page: 1,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['commissioning', 'records', filters],
        queryFn: () => api.get('/commissioning/records', { params: filters }).then((r) => r.data),
        placeholderData: (prev) => prev,
        ...COMMISSION_LIVE_QUERY,
    });

    const records: CommissionRecord[] = data?.data ?? [];
    const meta = data?.meta;

    const requestNfBulkMutation = useMutation({
        mutationFn: () => api.post('/commissioning/records/request-nf-bulk'),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'Cobranças enviadas!');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'records'] });
        },
        onError: () => toast.error('Erro ao enviar cobranças em lote.'),
    });

    const markPaidMutation = useMutation({
        mutationFn: (id: number) => api.post(`/commissioning/records/${id}/mark-paid`),
        onSuccess: () => {
            toast.success('Marcado como pago!');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'records'] });
            qc.invalidateQueries({ queryKey: ['commissioning', 'summary'] });
        },
        onError: () => toast.error('Erro ao marcar como pago.'),
    });

    const cancelMutation = useMutation({
        mutationFn: (id: number) => api.post(`/commissioning/records/${id}/cancel`),
        onSuccess: () => {
            toast.success('Cancelado!');
            setPendingAction(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'records'] });
        },
        onError: () => toast.error('Erro ao cancelar.'),
    });

    const confirmLoading =
        (pendingAction?.type === 'request_nf_bulk' && requestNfBulkMutation.isPending)
        || (pendingAction?.type === 'mark_paid' && markPaidMutation.isPending)
        || (pendingAction?.type === 'cancel' && cancelMutation.isPending);

    function handleConfirmAction() {
        if (!pendingAction) return;

        switch (pendingAction.type) {
            case 'request_nf_bulk':
                requestNfBulkMutation.mutate();
                break;
            case 'mark_paid':
                markPaidMutation.mutate(pendingAction.recordId);
                break;
            case 'cancel':
                cancelMutation.mutate(pendingAction.recordId);
                break;
        }
    }

    const showAwaitingNf = filters.status === 'awaiting_nf';
    const modal = pendingAction ? modalCopy(pendingAction) : null;

    return (
        <div className="space-y-6">
            <PageHeader title="Registros de Comissão" subtitle="Todos os registros gerados por deals ganhos">
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/commissioning/monthly-nfs"
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                    >
                        NFs Mensais (por vendedor)
                    </Link>
                    {filters.status === 'nf_submitted' && (
                        <Link
                            to="/commissioning/payment-approvals"
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                        >
                            Aprovação RH
                        </Link>
                    )}
                    {showAwaitingNf && records.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setPendingAction({
                                type: 'request_nf_bulk',
                                count: meta?.total ?? records.length,
                            })}
                            disabled={requestNfBulkMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                            <PiEnvelopeSimpleBold className="w-4 h-4" />
                            Cobrar NF (todos)
                        </button>
                    )}
                    <Link
                        to="/commissioning/nf-email-preview"
                        className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Ver e-mails de NF
                    </Link>
                </div>
            </PageHeader>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3">
                <select
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.status}
                    onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value, page: 1 }))}
                >
                    <option value="">Todos os status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.role}
                    onChange={(e) => setFilters((p) => ({ ...p, role: e.target.value, page: 1 }))}
                >
                    <option value="">Todos os níveis</option>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input
                    type="date"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.from}
                    onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value, page: 1 }))}
                />
                <input
                    type="date"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.to}
                    onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value, page: 1 }))}
                />
                {meta && (
                    <span className="text-sm text-gray-500 self-center ml-auto">
                        {meta.total} registros
                        {meta.totals && (
                            <> · Pendente: <strong>{fmt(meta.totals.total_pending)}</strong>
                            · Pago: <strong>{fmt(meta.totals.total_paid)}</strong></>
                        )}
                    </span>
                )}
            </div>

            {isLoading && <LoadingState variant="table" />}

            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium">Deal</th>
                            <th className="px-4 py-3 text-left font-medium">Usuário</th>
                            <th className="px-4 py-3 text-left font-medium">Nível</th>
                            <th className="px-4 py-3 text-right font-medium">Comissão</th>
                            <th className="px-4 py-3 text-left font-medium">Status</th>
                            <th className="px-4 py-3 text-left font-medium">Data</th>
                            <th className="px-4 py-3 text-left font-medium">Prazo NF</th>
                            <th className="px-4 py-3 text-center font-medium">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {records.map((r) => (
                            <tr key={r.id} className={`hover:bg-gray-50 ${r.confidential ? 'bg-red-50' : ''}`}>
                                <td className="px-4 py-3">
                                    <p className="font-medium text-gray-900">{r.deal_number ?? `#${r.ploomes_deal_id}`}</p>
                                    {r.deal_title && <p className="text-xs text-gray-500 truncate max-w-[180px]">{r.deal_title}</p>}
                                    {r.deal_amount && <p className="text-xs text-gray-400">{fmt(r.deal_amount)}</p>}
                                </td>
                                <td className="px-4 py-3 font-medium">{r.user_name}</td>
                                <td className="px-4 py-3">
                                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                        {r.role_label}
                                    </span>
                                    {r.confidential && <span className="ml-1 text-xs text-red-600">🔒</span>}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold">{fmt(r.final_amount)}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[r.status] ?? 'bg-gray-100 text-gray-700'}`}>
                                        {r.status_label}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs">{r.created_at.split(' ')[0]}</td>
                                <td className="px-4 py-3 text-gray-500 text-xs">
                                    {r.status === 'awaiting_nf' && r.days_until_nf_deadline != null ? (
                                        <span className={r.days_until_nf_deadline <= 3 ? 'text-red-600 font-medium' : ''}>
                                            {r.days_until_nf_deadline} dia(s)
                                            {r.nf_deadline_at && (
                                                <span className="block text-gray-400">até {r.nf_deadline_at.split(' ')[0]}</span>
                                            )}
                                        </span>
                                    ) : '—'}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1">
                                        <Link
                                            to={`/commissioning/sellers/${r.user_id}`}
                                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Ver histórico"
                                        >
                                            <PiEyeBold className="w-4 h-4" />
                                        </Link>
                                        {r.status === 'nf_submitted' && (
                                            <button
                                                type="button"
                                                onClick={() => setPendingAction({ type: 'mark_paid', recordId: r.id })}
                                                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                title="Marcar como pago"
                                            >
                                                <PiCheckBold className="w-4 h-4" />
                                            </button>
                                        )}
                                        {!['paid', 'cancelled'].includes(r.status) && (
                                            <button
                                                type="button"
                                                onClick={() => setPendingAction({ type: 'cancel', recordId: r.id })}
                                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Cancelar"
                                            >
                                                <PiXBold className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {records.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">
                                    Nenhum registro encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {meta && (
                <TablePagination
                    meta={meta}
                    onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
                    onPerPageChange={(per_page) => setFilters((f) => ({ ...f, per_page, page: 1 }))}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="registro"
                />
            )}

            {modal && (
                <ConfirmModal
                    open={pendingAction !== null}
                    onClose={() => !confirmLoading && setPendingAction(null)}
                    onConfirm={handleConfirmAction}
                    title={modal.title}
                    description={modal.description}
                    confirmLabel={modal.confirmLabel}
                    loading={confirmLoading}
                />
            )}
        </div>
    );
}
